import { useCallback } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { APP_SHELL_MESSAGE, isAppShell, readJson, serverErrorMessage } from "../lib/apiError";
import { useCachedQuery } from "./useCachedQuery";

// Mirrors SiteLoginView on the backend. Deliberately no password field:
// site logins are write-only for the customer, and no endpoint anywhere
// returns one. The mcp-gateway is the only reader, and it returns page
// text rather than the credential.
export type SiteLogin = {
  name: string;
  /** The one website this sign-in is ever used against. See SaveSiteLoginInput. */
  site: string;
  username: string;
  loginUrl: string | null;
  usernameField: string | null;
  passwordField: string | null;
  updatedAt: string;
};

// The three overrides are `| undefined` explicitly, not just optional:
// exactOptionalPropertyTypes is on, so a caller that always builds the
// object and leaves a field undefined couldn't otherwise pass it.
// JSON.stringify drops undefined values, so the wire body is the same.
export type SaveSiteLoginInput = {
  name: string;
  /**
   * The website this sign-in belongs to, e.g. portal.acme.com. Required, and
   * load-bearing: the platform will use this login against this site and
   * nowhere else, which is what stops a page fetch being talked into posting
   * the password somewhere the customer never named.
   */
  site: string;
  username: string;
  password: string;
  loginUrl?: string | undefined;
  usernameField?: string | undefined;
  passwordField?: string | undefined;
};

export const SITE_LOGIN_MAX_NAME_LENGTH = 60;
// Mirrors ztc.site-logins.max-per-tenant. There is a SECOND server-side cap
// the client deliberately does not mirror - a UTF-8 BYTE budget on the whole
// SSM document, which applies to all of a tenant's logins together, so the
// client can't usefully pre-check it and just surfaces the server's message.
export const SITE_LOGIN_MAX_PER_TENANT = 10;

export type SiteLoginsStatus = "loading" | "ready" | "error";

export type SiteLoginsResult<T> =
  | { ok: true; value: T }
  | { ok: false; message: string };

export type UseSiteLoginsResult = {
  status: SiteLoginsStatus;
  logins: ReadonlyArray<SiteLogin>;
  errorMessage: string | null;
  refetch: () => Promise<void>;
  saveLogin: (input: SaveSiteLoginInput) => Promise<SiteLoginsResult<SiteLogin>>;
  deleteLogin: (name: string) => Promise<SiteLoginsResult<void>>;
};

const listUrl = (tenantId: string): string => `/api/claws/${encodeURIComponent(tenantId)}/site-logins`;

export const useSiteLogins = (tenantId: string | null): UseSiteLoginsResult => {
  // No instance selected yet: hold on the loading state rather than firing a
  // request at /api/claws//site-logins.
  const queryKey = tenantId === null ? "" : listUrl(tenantId);

  const { status, data, error, refetch, mutate } = useCachedQuery<ReadonlyArray<SiteLogin>>({
    cacheResource: tenantId === null ? null : `site-logins:${tenantId}`,
    queryKey,
    fetcher: useCallback(
      async (signal: AbortSignal): Promise<ReadonlyArray<SiteLogin>> => {
        if (tenantId === null) return [];
        const res = await fetch(listUrl(tenantId), { credentials: "include", signal });
        /* Not `res.ok` alone: the CDN turns 403 and 404 into 200 with the app
         * shell, so a 200 here is not a promise that the body is JSON. See
         * isAppShell. */
        return await readJson<ReadonlyArray<SiteLogin>>(res, "Failed to load website sign-ins");
      },
      [tenantId],
    ),
  });

  const saveLogin = useCallback(
    async (input: SaveSiteLoginInput): Promise<SiteLoginsResult<SiteLogin>> => {
      if (tenantId === null) return { ok: false, message: "No instance selected." };
      try {
        const res = await fetch(listUrl(tenantId), {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify(input),
        });
        if (isAppShell(res)) return { ok: false, message: APP_SHELL_MESSAGE };
        if (!res.ok) return { ok: false, message: await serverErrorMessage(res, "Save failed") };
        const saved = (await res.json()) as SiteLogin;
        // Write-through, matching the server's create-or-replace: same name
        // replaces in place (keeping list order stable), a new one appends.
        mutate((prev) => {
          const idx = prev.findIndex((l) => l.name.toLowerCase() === saved.name.toLowerCase());
          if (idx < 0) return [...prev, saved];
          const next = [...prev];
          next[idx] = saved;
          return next;
        });
        return { ok: true, value: saved };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [tenantId, mutate],
  );

  const deleteLogin = useCallback(
    async (name: string): Promise<SiteLoginsResult<void>> => {
      if (tenantId === null) return { ok: false, message: "No instance selected." };
      try {
        const res = await fetch(`${listUrl(tenantId)}/${encodeURIComponent(name)}`, {
          method: "DELETE",
          credentials: "include",
          headers: getCsrfHeader(),
        });
        /* Checked before the status, because the CDN's rewrite makes this a
         * 200 and the envelope parse then yields "Delete failed (200)" - a
         * sentence that sends somebody looking for a bug in the delete. */
        if (isAppShell(res)) return { ok: false, message: APP_SHELL_MESSAGE };
        if (res.status !== 204) {
          return { ok: false, message: await serverErrorMessage(res, "Delete failed") };
        }
        mutate((prev) => prev.filter((l) => l.name !== name));
        return { ok: true, value: undefined };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [tenantId, mutate],
  );

  return {
    status: tenantId === null ? "loading" : status,
    logins: data ?? [],
    errorMessage: error,
    refetch,
    saveLogin,
    deleteLogin,
  };
};
